import React, { useRef, useEffect, useState, useImperativeHandle, forwardRef } from 'react';

export const SignaturePad = forwardRef(function SignaturePad({ width = 600, height = 200 }, ref) {
    const canvasRef = useRef(null);
    const [drawing, setDrawing] = useState(false);
    const [hasDrawn, setHasDrawn] = useState(false);

    useEffect(() => {
        const canvas = canvasRef.current;
        if (!canvas) return;
        const ratio = window.devicePixelRatio || 1;
        canvas.width = width * ratio;
        canvas.height = height * ratio;
        canvas.style.width = '100%';
        canvas.style.height = `${height}px`;
        const ctx = canvas.getContext('2d');
        ctx.setTransform(1, 0, 0, 1, 0, 0);
        ctx.scale(ratio, ratio);
        ctx.fillStyle = '#ffffff';
        ctx.fillRect(0, 0, width, height);
        ctx.strokeStyle = '#0F172A';
        ctx.lineWidth = 2.2;
        ctx.lineCap = 'round';
        ctx.lineJoin = 'round';
    }, [width, height]);

    const getPoint = (e) => {
        const canvas = canvasRef.current;
        const rect = canvas.getBoundingClientRect();
        const scaleX = canvas.width / (window.devicePixelRatio || 1) / rect.width;
        const scaleY = canvas.height / (window.devicePixelRatio || 1) / rect.height;
        const point = e.touches ? e.touches[0] : e;
        return {
            x: (point.clientX - rect.left) * scaleX,
            y: (point.clientY - rect.top) * scaleY,
        };
    };

    const start = (e) => {
        e.preventDefault();
        const { x, y } = getPoint(e);
        const ctx = canvasRef.current.getContext('2d');
        ctx.beginPath();
        ctx.moveTo(x, y);
        setDrawing(true);
    };
    const move = (e) => {
        if (!drawing) return;
        e.preventDefault();
        const { x, y } = getPoint(e);
        const ctx = canvasRef.current.getContext('2d');
        ctx.lineTo(x, y);
        ctx.stroke();
        setHasDrawn(true);
    };
    const end = () => {
        setDrawing(false);
        const ctx = canvasRef.current.getContext('2d');
        ctx.closePath();
    };
    const clear = () => {
        const canvas = canvasRef.current;
        const ctx = canvas.getContext('2d');
        ctx.fillStyle = '#ffffff';
        ctx.fillRect(0, 0, canvas.width, canvas.height);
        setHasDrawn(false);
    };
    const getDataUrl = () => canvasRef.current?.toDataURL('image/png') || null;

    useImperativeHandle(ref, () => ({
        clear,
        getDataUrl,
        hasDrawn: () => hasDrawn,
    }));

    return (
        <div className="w-full" data-testid="signature-pad-wrapper">
            <canvas
                ref={canvasRef}
                className="signature-canvas"
                data-testid="quote-signature-canvas"
                onMouseDown={start}
                onMouseMove={move}
                onMouseUp={end}
                onMouseLeave={end}
                onTouchStart={start}
                onTouchMove={move}
                onTouchEnd={end}
            />
            <div className="flex justify-end mt-2">
                <button
                    type="button"
                    onClick={clear}
                    className="text-sm text-slate-600 hover:text-slate-900 underline"
                    data-testid="signature-clear-button"
                >
                    Clear
                </button>
            </div>
        </div>
    );
});
